import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { filesApi } from '@/services/files.api';
import { avatarColorFor } from './avatar-color';

function initials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * A person's photo, or their initials when there isn't one.
 *
 * `photoFileId` is a `FileObject` id, never a URL — the signed download URL
 * is fetched fresh here rather than carried on `PersonResponse`, so it's
 * never stale and nothing server-side needs to know this component exists.
 * Read-only; `person-detail-page.tsx` handles the upload interaction itself
 * and just passes the resulting `photoFileId` back down.
 */
export function Avatar({
  photoFileId,
  displayName,
  size = 40,
}: {
  readonly photoFileId: string | null;
  readonly displayName: string;
  readonly size?: number;
}): ReactNode {
  const download = useQuery({
    queryKey: ['files', 'download-url', photoFileId],
    queryFn: () => filesApi.getDownloadUrl(photoFileId!),
    enabled: photoFileId !== null,
    staleTime: 60_000,
  });

  const style = { width: size, height: size, fontSize: size * 0.4 };

  if (download.data) {
    return (
      <img
        src={download.data.url}
        alt=""
        className="avatar avatar-image"
        style={style}
        width={size}
        height={size}
      />
    );
  }

  const { background, foreground } = avatarColorFor(displayName);

  return (
    <span
      className="avatar avatar-initials"
      style={{ ...style, background, color: foreground }}
      aria-hidden="true"
    >
      {initials(displayName) || '?'}
    </span>
  );
}
